import { Cpu, Lightbulb, Zap } from 'lucide-react';
import type { Competition } from '../../data/competitions';
import { partsFor } from '../../data/parts';
import type { PartSpec } from '../../types';

interface Props {
  comp: Competition;
}

function PartCard({ part, accent }: { part: PartSpec; accent: string }) {
  return (
    <div className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-xl bg-white/5">
        {part.image ? (
          <img src={part.image} alt="" className="size-full object-contain p-1.5" />
        ) : (
          <Cpu className="size-6 text-slate-500" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-bold text-slate-100">{part.name}</h4>
          <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${accent}`}>
            {part.category}
          </span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-slate-400">{part.description}</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
          {part.voltage && <span>⚡ {part.voltage}</span>}
          {part.weight && <span>⚖ {part.weight}</span>}
          {part.price && <span>💲 {part.price}</span>}
        </div>
        {part.wiring && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
            {Object.entries(part.wiring).map(([k, v]) => (
              <span key={k}>
                <span className="uppercase text-slate-600">{k}</span>: {v}
              </span>
            ))}
          </div>
        )}
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-200/80">
          <Lightbulb className="mt-0.5 size-3 shrink-0" />
          {part.beginnerTip}
        </p>
        <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
          <Zap className="mt-0.5 size-3 shrink-0" />
          Simulation: {part.simulationBehavior}
        </p>
      </div>
    </div>
  );
}

export default function PartsTab({ comp }: Props) {
  const { required, optional } = partsFor(comp.id);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">
          Required parts for {comp.name}
        </h3>
        <p className="mt-1 text-sm text-slate-400">
          These cover the standard build. Check the rule pack's allowed-parts section before buying anything
          for an official event.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {required.map((p) => (
            <PartCard key={p.id} part={p} accent={comp.chip} />
          ))}
        </div>
      </div>

      {optional.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">Optional upgrades</h3>
          <p className="mt-1 text-sm text-slate-400">
            Not required to compete, but useful for tuning, telemetry, or advanced strategies.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {optional.map((p) => (
              <PartCard key={p.id} part={p} accent="bg-white/5 text-slate-400" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
